import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getCourse } from '../utils/api';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle, Upload, DollarSign, User, Briefcase, CreditCard, Globe } from 'lucide-react';
import {
  COUNTRIES, JOB_TITLES, INDUSTRIES, EDUCATION_LEVELS, EXPERIENCE_YEARS,
  GENDERS, LANGUAGES, REFERRAL_SOURCES, PAYMENT_LABELS,
} from '../utils/enrollmentOptions';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  // Personal
  student_name: '',
  student_email: '',
  student_phone: '',
  student_country: '',
  student_city: '',
  student_id_doc: '',
  student_birthdate: '',
  student_gender: '',
  // Profesional
  job_title: '',
  job_title_custom: '',
  organization: '',
  industry: '',
  experience_years: '',
  education_level: '',
  linkedin_url: '',
  preferred_language: '',
  referral_source: '',
  // Pago
  payment_method: '',
  message: '',
  accepted_terms: false,
};

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pt-2 pb-1 border-b mb-3">
    <Icon className="w-4 h-4 text-blue-600" />
    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">{title}</h3>
  </div>
);

const EnrollForm = ({ courseIdProp, embedMode = false }) => {
  const { courseId: courseIdParam } = useParams();
  const courseId = courseIdProp || courseIdParam;

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [scholarshipStats, setScholarshipStats] = useState(null);

  const [form, setForm] = useState(initialForm);
  const [proofFile, setProofFile] = useState(null);
  const proofRef = useRef();

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!courseId) return;
    getCourse(courseId)
      .then(r => setCourse(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load scholarship availability (public endpoint)
    api.get(`/enrollments/scholarship-stats/${courseId}`)
      .then(r => setScholarshipStats(r.data))
      .catch(() => setScholarshipStats(null));
  }, [courseId]);

  const enabledMethods = course?.payment_methods
    ? Object.entries(course.payment_methods).filter(([, v]) => v?.enabled)
    : [];

  const showScholarship = !!(scholarshipStats?.enabled && scholarshipStats?.remaining > 0);
  const isScholarship = form.payment_method === 'scholarship';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.student_email) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    if (!form.accepted_terms) {
      toast.error('Debes aceptar los términos para continuar');
      return;
    }
    if (form.job_title === 'Otro' && !form.job_title_custom.trim()) {
      toast.error('Especifica tu puesto');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('course_id', courseId);
      // Resolve job_title (use custom if user picked "Otro")
      const finalJobTitle = form.job_title === 'Otro' && form.job_title_custom
        ? form.job_title_custom
        : form.job_title;

      const payload = { ...form, job_title: finalJobTitle };
      delete payload.job_title_custom;

      Object.entries(payload).forEach(([k, v]) => {
        if (typeof v === 'boolean') fd.append(k, v ? 'true' : 'false');
        else fd.append(k, v == null ? '' : String(v));
      });
      if (proofFile) fd.append('payment_proof', proofFile);

      const res = await fetch(`${API_URL}/enrollments/request`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || 'Error al enviar');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center p-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
  );

  if (!course) return (
    <div className="p-8 text-center text-gray-500">Curso no encontrado</div>
  );

  if (submitted) return (
    <div className="flex flex-col items-center justify-center p-12 text-center gap-4" data-testid="enroll-success">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h2 className="text-xl font-bold text-gray-800">¡Solicitud Enviada!</h2>
      <p className="text-gray-600 max-w-sm">
        Tu solicitud de inscripción para <strong>{course.title}</strong> fue recibida.
        El equipo de IAASP Academy se pondrá en contacto contigo pronto.
      </p>
    </div>
  );

  return (
    <div className={embedMode ? 'bg-white' : 'min-h-screen bg-gray-50 py-10'} data-testid="enroll-form-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Course Header */}
        <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6">
          {course.image_url && (
            <img src={course.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${course.image_url}` : course.image_url}
              alt={course.title} className="w-full h-40 object-cover rounded-xl mb-4" />
          )}
          <h1 className="text-xl font-bold text-gray-900 mb-1">{course.title}</h1>
          <p className="text-sm text-gray-500 mb-3 line-clamp-2">{course.description}</p>
          <div className="flex items-center gap-3 flex-wrap">
            {course.is_free ? (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">Gratis</span>
            ) : (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                <DollarSign className="w-3.5 h-3.5" />USD ${Number(course.price || 0).toFixed(2)}
              </span>
            )}
            {course.delivery_mode === 'zoom' && (
              <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold">Zoom</span>
            )}
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
              <Globe className="w-3 h-3" />Inscripción internacional
            </span>
          </div>

          {course.instructor_whatsapp && (() => {
            const phone = course.instructor_whatsapp.replace(/[^\d+]/g, '').replace(/^\+/, '');
            const msg = encodeURIComponent(course.instructor_whatsapp_message || `Hola, tengo interés en el curso "${course.title}". ¿Me puedes dar más información?`);
            return (
              <a href={`https://wa.me/${phone}?text=${msg}`} target="_blank" rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-semibold text-sm transition-colors w-full sm:w-auto justify-center"
                data-testid="enroll-whatsapp-instructor">
                <span className="inline-block w-5 h-5 rounded-full bg-white text-green-500 text-xs font-bold flex items-center justify-center">W</span>
                Contactar al instructor por WhatsApp
              </a>
            );
          })()}
        </div>

        {/* Payment Info */}
        {!course.is_free && enabledMethods.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border p-6 mb-6">
            <h2 className="font-semibold text-gray-800 mb-3">Métodos de Pago Disponibles</h2>
            <div className="space-y-3">
              {enabledMethods.map(([key, val]) => (
                <div key={key} className="p-3 rounded-xl bg-gray-50 border text-sm">
                  <p className="font-semibold text-gray-700 mb-1">{PAYMENT_LABELS[key] || key}</p>
                  {key === 'paypal' && val.link && <p>Link: <a href={val.link} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{val.link}</a></p>}
                  {key === 'sinpe' && <p>Número: <strong>{val.number}</strong> — {val.name}</p>}
                  {key === 'nequi' && <p>Número: <strong>{val.number}</strong></p>}
                  {key === 'daviplata' && <p>Número: <strong>{val.number}</strong></p>}
                  {key === 'mercadopago' && val.link && <p>Link: <a href={val.link} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{val.link}</a></p>}
                  {key === 'yappy' && <p>Número: <strong>{val.number}</strong></p>}
                  {key === 'bank_transfer' && <p className="whitespace-pre-line">{val.details}</p>}
                  {key === 'offline' && <p className="whitespace-pre-line">{val.details || 'Pago en efectivo o en oficina. Coordina con el equipo para más detalles.'}</p>}
                  {key === 'other' && <><p className="font-medium">{val.name}</p><p className="whitespace-pre-line">{val.details}</p></>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enrollment Form */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="font-semibold text-gray-800 mb-1">Formulario de Inscripción</h2>
          <p className="text-xs text-gray-500 mb-5">Los campos marcados con * son obligatorios.</p>

          {course.course_state === 'finished' ? (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center" data-testid="enroll-closed-banner">
              <div className="text-4xl mb-2">⛔</div>
              <h3 className="text-lg font-semibold text-red-700 mb-1">Inscripciones cerradas</h3>
              <p className="text-sm text-red-600/90">
                Este curso finalizó el {course.end_date ? new Date(`${course.end_date}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : 'recientemente'}.
                Si necesitas más información o quieres ser notificado/a cuando vuelva a abrir, contacta al administrador o al instructor.
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* DATOS PERSONALES */}
            <SectionTitle icon={User} title="Datos personales" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nombre completo *</Label>
                <Input value={form.student_name} onChange={e => update('student_name', e.target.value)}
                  placeholder="Tu nombre completo" required data-testid="enroll-name" />
              </div>
              <div>
                <Label>Correo electrónico *</Label>
                <Input type="email" value={form.student_email} onChange={e => update('student_email', e.target.value)}
                  placeholder="tu@email.com" required data-testid="enroll-email" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Teléfono (con código país)</Label>
                <Input value={form.student_phone} onChange={e => update('student_phone', e.target.value)}
                  placeholder="+506 8888-8888" data-testid="enroll-phone" />
              </div>
              <div>
                <Label>País de residencia</Label>
                <Select value={form.student_country} onValueChange={v => update('student_country', v)}>
                  <SelectTrigger data-testid="enroll-country"><SelectValue placeholder="Seleccionar país..." /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Ciudad</Label>
                <Input value={form.student_city} onChange={e => update('student_city', e.target.value)}
                  placeholder="Ciudad o región" />
              </div>
              <div>
                <Label>Documento de identidad</Label>
                <Input value={form.student_id_doc} onChange={e => update('student_id_doc', e.target.value)}
                  placeholder="Cédula, pasaporte, DNI..." />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Fecha de nacimiento</Label>
                <Input type="date" value={form.student_birthdate}
                  onChange={e => update('student_birthdate', e.target.value)} />
              </div>
              <div>
                <Label>Género</Label>
                <Select value={form.student_gender} onValueChange={v => update('student_gender', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* DATOS PROFESIONALES */}
            <SectionTitle icon={Briefcase} title="Datos profesionales" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Cargo / Puesto actual</Label>
                <Select value={form.job_title} onValueChange={v => update('job_title', v)}>
                  <SelectTrigger data-testid="enroll-job-title"><SelectValue placeholder="Selecciona tu puesto..." /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {JOB_TITLES.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organización / Empresa</Label>
                <Input value={form.organization} onChange={e => update('organization', e.target.value)}
                  placeholder="Nombre de tu empresa o institución" />
              </div>
            </div>

            {form.job_title === 'Otro' && (
              <div>
                <Label>Especifica tu puesto</Label>
                <Input value={form.job_title_custom} onChange={e => update('job_title_custom', e.target.value)}
                  placeholder="Describe tu cargo o puesto" />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Sector / Industria</Label>
                <Select value={form.industry} onValueChange={v => update('industry', v)}>
                  <SelectTrigger><SelectValue placeholder="Selecciona el sector..." /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Años de experiencia</Label>
                <Select value={form.experience_years} onValueChange={v => update('experience_years', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nivel educativo más alto</Label>
                <Select value={form.education_level} onValueChange={v => update('education_level', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {EDUCATION_LEVELS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Idioma preferido</Label>
                <Select value={form.preferred_language} onValueChange={v => update('preferred_language', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Perfil de LinkedIn (opcional)</Label>
                <Input type="url" value={form.linkedin_url} onChange={e => update('linkedin_url', e.target.value)}
                  placeholder="https://linkedin.com/in/tu-perfil" />
              </div>
              <div>
                <Label>¿Cómo nos conoció?</Label>
                <Select value={form.referral_source} onValueChange={v => update('referral_source', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {REFERRAL_SOURCES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* PAGO */}
            {!course.is_free && (
              <>
                <SectionTitle icon={CreditCard} title="Información de pago" />

                {showScholarship && (
                  <div className="border-2 border-amber-300 bg-amber-50 rounded-xl p-3 text-sm" data-testid="scholarship-banner">
                    🎓 <strong>¡Becas disponibles!</strong> Quedan {scholarshipStats.remaining} cupo(s) de beca para este curso.
                    Puedes seleccionar "Beca" como método de pago si calificas.
                  </div>
                )}

                {(enabledMethods.length > 0 || showScholarship) && (
                  <div>
                    <Label>Método de pago utilizado</Label>
                    <Select value={form.payment_method} onValueChange={v => update('payment_method', v)}>
                      <SelectTrigger data-testid="enroll-payment-method"><SelectValue placeholder="¿Cómo pagaste?" /></SelectTrigger>
                      <SelectContent>
                        {enabledMethods.map(([key]) => (
                          <SelectItem key={key} value={key}>{PAYMENT_LABELS[key] || key}</SelectItem>
                        ))}
                        {showScholarship && (
                          <SelectItem value="scholarship" data-testid="enroll-scholarship-option">
                            🎓 Beca / Becado (sin pago)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {!isScholarship && (
                  <div>
                    <Label>Comprobante de pago</Label>
                    <div
                      onClick={() => proofRef.current?.click()}
                      className="mt-1 border-2 border-dashed border-gray-300 rounded-xl p-4 text-center cursor-pointer hover:border-blue-400 transition-colors"
                      data-testid="enroll-proof-upload"
                    >
                      <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                      <p className="text-sm text-gray-500">
                        {proofFile ? proofFile.name : 'Clic para adjuntar imagen o PDF de pago'}
                      </p>
                      <input ref={proofRef} type="file" accept="image/*,.pdf" className="hidden"
                        onChange={e => setProofFile(e.target.files?.[0] || null)} />
                    </div>
                  </div>
                )}

                {isScholarship && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm" data-testid="scholarship-info">
                    📋 Como solicitas una <strong>beca</strong>, no necesitas adjuntar comprobante.
                    Tu solicitud quedará pendiente de aprobación por el administrador del curso.
                  </div>
                )}
              </>
            )}

            {/* MENSAJE */}
            <div>
              <Label>Mensaje o pregunta (opcional)</Label>
              <Textarea value={form.message} onChange={e => update('message', e.target.value)}
                placeholder="¿Tienes alguna duda, comentario o solicitud especial?" rows={3} />
            </div>

            {/* CONSENTIMIENTO */}
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl border">
              <Checkbox
                id="accept_terms"
                checked={form.accepted_terms}
                onCheckedChange={v => update('accepted_terms', v === true)}
                data-testid="enroll-accept-terms"
              />
              <Label htmlFor="accept_terms" className="text-xs leading-relaxed cursor-pointer">
                Acepto que IAASP Academy procese mis datos personales para gestionar mi inscripción y enviarme
                comunicaciones relacionadas con el curso. Mis datos serán tratados de forma confidencial y
                conforme a la normativa de protección de datos aplicable.
              </Label>
            </div>

            <Button type="submit" className="w-full" disabled={submitting} data-testid="enroll-submit-btn">
              {submitting ? 'Enviando...' : 'Enviar Solicitud de Inscripción'}
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollForm;
